import { createRouter, createWebHistory } from 'vue-router';
import { i18n, loadLocale } from '@shared/i18n';
import { registerErrorRouter } from '@shared/composables/useErrorRouter';
import { ensureClubCredentials } from '../composables/useGuestAuth';
import { initApp } from '@shared/composables/useAppInit';
import { useUserStore } from '@shared/stores/user';

// Nur fuenf Locales — angular.json fuehrt fuer diese App genau diese (i18n.locales bzw.
// production `localize`), und der Header-Sprachumschalter bietet dieselben fuenf.
// NICHT die elf der anderen Apps.
const SUPPORTED_LOCALES = ['de', 'en', 'es', 'tr', 'uk'];

function detectLocale(): string {
    const current = i18n.global.locale.value;
    if (SUPPORTED_LOCALES.includes(current)) {
        return current;
    }
    const lang = navigator.language.slice(0, 2);
    return SUPPORTED_LOCALES.includes(lang) ? lang : 'de';
}

export const router = createRouter({
    // Reine Browser-SPA: immer History, nie Hash — die App wird nicht nativ gewrappt.
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior(_to, _from, savedPosition) {
        return savedPosition ?? { top: 0 };
    },
    routes: [
        {
            path: '/',
            redirect: () => `/${detectLocale()}/`,
        },
        {
            // Faengt locale-lose Pfade ab. Die Locale-Gruppe ist bewusst auf die fuenf
            // unterstuetzten eingeschraenkt, sonst schluckte `:locale` auch `:clubId`.
            path: '/:pathMatch(.*)*',
            redirect: (to) => {
                const segments = Array.isArray(to.params.pathMatch) ? to.params.pathMatch.join('/') : to.params.pathMatch;
                const first = segments.split('/')[0];
                if (SUPPORTED_LOCALES.includes(first)) {
                    return { path: '/', query: to.query, hash: to.hash };
                }
                return { path: `/${detectLocale()}/${segments}`, query: to.query, hash: to.hash };
            },
        },
        {
            path: '/:locale(de|en|es|tr|uk)',
            children: [
                {
                    path: '',
                    component: () => import('../views/HomeView.vue'),
                },
                {
                    // Angular: `{ path: ':clubId', redirectTo: ':clubId/offerings' }`.
                    // `\\d+` statt freiem Platzhalter: Angular parst die ID ohnehin mit `parseInt`,
                    // alles andere endet dort in `NaN`. Die Einschraenkung haelt ausserdem spaetere
                    // statische Geschwister (`/error`) erreichbar (APP_MIGRATE_CLUB_OFFERING.md 8.6).
                    path: ':clubId(\\d+)',
                    redirect: (to) => `/${to.params.locale as string}/${to.params.clubId as string}/offerings`,
                },
                {
                    // app.routes.ts — Deep-Link-Ziel der App-/Universal-Links. Kein Guard.
                    //
                    // NAMENSKOLLISION: denselben Pfad meldet auch `UserRoutingModule` an, dort mit
                    // `AuthGuard` und der Member-Token-Variante der Komponente. In Angular gewinnt
                    // die app-lokale Route, weil `provideRouter(routes)` vor
                    // `importProvidersFrom([UserRoutingModule])` greift. Deshalb existiert hier
                    // GENAU EINE Route — die Gast-Variante ohne Guard.
                    path: 'federation/:federationId/institution/:institutionId/redirect',
                    component: () => import('../views/InstitutionRedirectView.vue'),
                },
                {
                    // customer-apps/dog/dog-routing.module.ts, in app.routes.ts als
                    // `{ path: 'dogs', canActivate: [LoggedInAndUsersLoadedGuard] }` eingehaengt.
                    // Der Guard ist AuthGuard + WaitForUserGuard — Login UND geladener mainUser.
                    path: 'dogs',
                    meta: { requiresAuth: true, requiresUsers: true },
                    children: [
                        // KEIN ''-Kind: die Hundeliste ist in dieser App unerreichbar und
                        // unverlinkt (in Angular von `:clubId` verschattet, gemessen) und wird
                        // bewusst nicht portiert — APP_MIGRATE_CLUB_OFFERING.md 8.6.
                        {
                            path: 'create',
                            component: () => import('@shared/views/dogs/CreateDogView.vue'),
                        },
                        {
                            path: ':id',
                            component: () => import('@shared/views/dogs/DogDetailView.vue'),
                        },
                    ],
                },
                // customer-apps/user/user-routing.module.ts — in dieser App ueber
                // `importProvidersFrom([UserRoutingModule])` in app.config.ts registriert, nicht
                // ueber app.routes.ts. Guards 1:1: die `user/*`-Routen haben keinen, der
                // Institutions-Redirect traegt den AuthGuard.
                {
                    path: 'user/login',
                    component: () => import('@shared/views/user/UserLoginView.vue'),
                },
                {
                    path: 'user/switch',
                    component: () => import('@shared/views/user/SwitchUserView.vue'),
                },
                {
                    path: 'user/logout',
                    component: () => import('@shared/views/user/LogoutView.vue'),
                },
                {
                    path: 'user/password/reset',
                    component: () => import('@shared/views/user/PasswordResetFlowView.vue'),
                },
                {
                    path: 'user/password/reset/:token',
                    component: () => import('@shared/views/user/PasswordResetFlowView.vue'),
                },
                {
                    // Angular-Kommentar: „legacy redirect route - can be deleted once all apps have
                    // been updated and deployed".
                    path: 'institution/:institutionId',
                    component: () => import('@shared/views/user/InstitutionRedirectView.vue'),
                    meta: { requiresAuth: true },
                },
                {
                    // Angular: `{ path: ':clubId/user', canActivate: [RequireClubUserCredentialsGuard],
                    // loadChildren: UserRoutingModule }` mit genau einem Kind.
                    path: ':clubId(\\d+)/user',
                    meta: { requiresClubCredentials: true },
                    children: [
                        {
                            path: 'guest-registration',
                            component: () => import('../views/GuestRegistrationView.vue'),
                        },
                    ],
                },
                {
                    path: ':clubId(\\d+)/offerings',
                    // Angular: `canActivate` UND `canActivateChild` des
                    // RequireClubUserCredentialsGuard. Das Flag wird im globalen `beforeEach`
                    // ausgewertet, nicht per `beforeEnter`: wechselt innerhalb des Teilbaums nur
                    // die `:clubId`, gilt der Record fuer Vue Router als "updating" und
                    // `beforeEnter` feuert nicht mehr — Angular prueft dort sehr wohl erneut.
                    meta: { requiresClubCredentials: true },
                    children: [
                        {
                            path: 'generic-offerings',
                            redirect: (to) => `/${to.params.locale as string}/${to.params.clubId as string}/offerings`,
                        },
                        {
                            path: '',
                            component: () => import('../views/OfferingsView.vue'),
                        },
                        {
                            path: 'courses',
                            component: () => import('../views/OfferingsView.vue'),
                            meta: { categoryNameFilter: 'course' },
                        },
                        {
                            path: 'exams',
                            component: () => import('../views/OfferingsView.vue'),
                            meta: { categoryNameFilter: 'exam' },
                        },
                        {
                            path: 'trainings',
                            component: () => import('../views/OfferingsView.vue'),
                            // Angular hatte hier `'trainings'` stehen und filterte damit ins Leere;
                            // der Tippfehler ist im Original gefixt (APP_MIGRATE_CLUB_OFFERING.md 8.4).
                            meta: { categoryNameFilter: 'training' },
                        },
                    ],
                },
                {
                    // ErrorRoutingModule. In Angular sind diese beiden Routen TOT: app.config.ts
                    // listet `ErrorRoutingModule` direkt im `providers`-Array statt in
                    // `importProvidersFrom(...)`, seine `RouterModule.forChild`-Routen werden also
                    // nie registriert (`/angebote/error` wird zusaetzlich von `:clubId`
                    // verschattet). Bewusst nicht nachgebaut: `useErrorRouter` navigiert aktiv
                    // hierher, ohne Ziel saehe der Nutzer im Fehlerfall eine leere Seite.
                    // Entscheidung und Messung: APP_MIGRATE_CLUB_OFFERING.md 8.5 und 8.6.
                    path: 'error',
                    component: () => import('@shared/views/error/ErrorPageView.vue'),
                },
                {
                    // Angular `{ path: '**', component: ErrorPageComponent }` — muss die LETZTE
                    // Route unter `/:locale` bleiben.
                    path: ':pathMatch(.*)*',
                    component: () => import('@shared/views/error/ErrorPageView.vue'),
                },
            ],
        },
    ],
});

// Angular injiziert `ErrorRouter` ueberall dort, wo er gebraucht wird; das shared-Pendant braucht
// die Instanz. Ohne diesen Aufruf ist `redirectToError()` eine stille Attrappe — relevant, seit
// `initApp()` am `/dogs`-Zweig laeuft und im Fehlerfall genau dorthin leiten will.
registerErrorRouter(router);

router.beforeEach(async (to) => {
    const locale = to.params.locale as string | undefined;
    if (locale && SUPPORTED_LOCALES.includes(locale)) {
        await loadLocale(locale);
    }
    // 1:1 zu `RequireClubUserCredentialsGuard`: passt die Institution nicht, wird ein Gast-Token
    // geholt. Der Aufruf ist auch bei unveraenderter Institution guenstig — `ensureClubCredentials`
    // steigt dann sofort aus, genau wie Angulars `of(userCredentials)`-Zweig.
    if (to.matched.some((record) => record.meta.requiresClubCredentials)) {
        return ensureClubCredentials(Number(to.params.clubId));
    }

    // Angular `AuthGuard` (customer-apps/user/services/auth.guard.ts) — haengt in dieser App nur am
    // Institutions-Redirect. Sein `layerLogin()`-Zweig fuer eine laufende In-App-Navigation ist hier
    // nicht nachgebaut: die Route ist ein reines Deep-Link-Ziel, nichts in der App verlinkt sie,
    // dieser Zweig ist also unerreichbar. Ziel ist Angulars eigenes `/user/login`.
    if (to.meta.requiresAuth && !useUserStore().isSessionValid) {
        const locale = (to.params.locale as string | undefined) ?? detectLocale();
        return { path: `/${locale}/user/login`, query: { redirect: to.fullPath } };
    }

    // Angular `WaitForUserGuard`: `filter(mainUser => !!mainUser)` — die Aktivierung wartet, bis
    // `mainUser$` einen Wert hat.
    if (to.meta.requiresUsers) {
        const userStore = useUserStore();
        // Das Gate MUSS auf der JWT-`userId` sitzen, nicht auf `isLoggedIn`. Angulars `UserService`
        // startet `fetchUsers()` nur `if (credentials && credentials.userId)` (user.service.ts:59),
        // und das anonyme Gast-JWT traegt `userId: null` — fuer den Gast emittiert `mainUser$` also
        // nie und die Navigation bleibt schlicht stehen. Das ist erreichbar: das Buchungsformular
        // verlinkt `/dogs/create` auch im Gast-Zweig (`@if (askDogs)`).
        //
        // Wuerde hier stattdessen `initApp()` laufen, antwortete `GET /users` dem Gast mit 403, und
        // `useAppInit` leitete auf die Fehlerseite um — der Gast saehe einen harten Fehler, wo
        // Angular gar nichts tut. Dieselbe Falle ist in `useGuestAuth.ts` schon dokumentiert.
        if (!userStore.mainUserId) {
            return false;
        }
        if (!userStore.currentUser) {
            await initApp();
        }
    }

    return true;
});
